import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMemberAccessToken } from '../auth/supabase';
import { prefetchOfflineInventory } from './offlineInventoryCache';
import './deployment-expiration-gate.css';

function formatExpiration(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || '').trim());
  if (!match) return value || 'Unknown';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function blockerTiming(blocker) {
  if (blocker.kind === 'short') return blocker.message || `Found ${blocker.found || 0} of ${blocker.required || 0}`;
  if (blocker.kind === 'needs_review') return 'Expiration needs review';
  if (blocker.kind === 'standard_missing') return 'Inventory standard missing';
  if (blocker.kind === 'expired' || blocker.expired) return 'EXPIRED';
  if (blocker.kind === 'expiring') {
    if (blocker.daysRemaining === 0) return 'Expires today';
    if (blocker.daysRemaining === 1) return 'Expires tomorrow';
    return `Expires in ${blocker.daysRemaining} days`;
  }
  return blocker.message || 'Not deployment ready';
}

function blockerDate(blocker) {
  if (blocker.kind === 'short') return `${blocker.found || 0}/${blocker.required || 0}`;
  if (blocker.kind === 'needs_review' || blocker.kind === 'standard_missing') return 'REVIEW';
  return formatExpiration(blocker.expirationDate);
}

export default function DeploymentExpirationGate({ assetIds, onStatusChange }) {
  const ids = useMemo(
    () => [...new Set((assetIds || []).map(item => String(item || '').trim().toUpperCase()).filter(Boolean))].sort(),
    [assetIds],
  );
  const requestRef = useRef(0);
  const [status, setStatus] = useState('idle');
  const [blockers, setBlockers] = useState([]);
  const [warningDays, setWarningDays] = useState(30);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const setGateStatus = useCallback((nextStatus, nextBlockers = [], nextError = '') => {
    setStatus(nextStatus);
    setBlockers(nextBlockers);
    setError(nextError);
    onStatusChange?.(nextStatus);
  }, [onStatusChange]);

  const checkReadiness = useCallback(async (showPopup = false) => {
    const requestId = ++requestRef.current;
    if (!ids.length) {
      setModalOpen(false);
      setGateStatus('idle', []);
      return;
    }

    setGateStatus('checking', []);
    try {
      const token = await getMemberAccessToken();
      if (!token) throw new Error('Member sign in required.');
      const query = encodeURIComponent(ids.join(','));
      const response = await fetch(`/api/inventory-readiness?assetIds=${query}`, {
        cache: 'no-store',
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Unable to verify inventory readiness.');
      if (requestRef.current !== requestId) return;

      // While the selected deployment gear is online, also save each supported bag's
      // inventory on this device so field usage can still be recorded with no service.
      prefetchOfflineInventory(ids).catch(() => {});

      const nextBlockers = Array.isArray(payload?.blockers) ? payload.blockers : [];
      setWarningDays(Number(payload?.warningDays) || 30);
      if (payload?.blocking || nextBlockers.length) {
        setGateStatus('blocked', nextBlockers);
        if (showPopup) setModalOpen(true);
      } else {
        setModalOpen(false);
        setGateStatus('clear', []);
      }
    } catch (err) {
      if (requestRef.current !== requestId) return;
      setModalOpen(false);
      setGateStatus('unavailable', [], err?.message || 'Inventory readiness check unavailable.');
    }
  }, [ids, setGateStatus]);

  useEffect(() => {
    checkReadiness(true);
    return () => { requestRef.current += 1; };
  }, [checkReadiness]);

  if (!ids.length) return null;

  return (
    <>
      {status === 'checking' && (
        <div className="tracking-expiration-status checking">Checking selected gear against the live inventory standard…</div>
      )}

      {status === 'unavailable' && (
        <div className="tracking-expiration-status unavailable">
          <strong>Inventory readiness check unavailable.</strong>
          <span>{error} Verify the bag manually before deployment.</span>
          <button type="button" onClick={() => checkReadiness(false)}>Try again</button>
        </div>
      )}

      {status === 'blocked' && (
        <section className="tracking-expiration-status blocked" aria-live="assertive">
          <div>
            <strong>Selected gear is not deployment ready</strong>
            <span>{blockers.length} inventory issue{blockers.length === 1 ? '' : 's'} must be cleared. Missing/short stock, expired or unknown inventory, and items expiring within {warningDays} days all block checkout.</span>
          </div>
          <button type="button" onClick={() => checkReadiness(false)}>Refresh after correction</button>
        </section>
      )}

      {modalOpen && status === 'blocked' && (
        <div className="tracking-expiration-modal-backdrop" role="presentation">
          <section className="tracking-expiration-modal" role="alertdialog" aria-modal="true" aria-labelledby="tracking-expiration-title">
            <div className="tracking-expiration-modal-head">
              <div>
                <span className="tracking-expiration-kicker">DEPLOYMENT STOP</span>
                <h3 id="tracking-expiration-title">Inventory is not ready</h3>
                <p>The selected gear does not meet the live inventory standard. Correct the listed shortage, expiration, or review issue, update inventory, then refresh the check.</p>
              </div>
              <button type="button" className="tracking-expiration-close" onClick={() => setModalOpen(false)} aria-label="Close warning">×</button>
            </div>

            <div className="tracking-expiration-list">
              {blockers.map((blocker, index) => (
                <div className="tracking-expiration-item" key={blocker.recordId || `${blocker.assetId}-${blocker.catalogId}-${blocker.kind}-${index}`}>
                  <div>
                    <strong>{blocker.item || blocker.catalogId || 'Inventory requirement'}</strong>
                    <small>{blocker.assetName || blocker.assetId}{blocker.component ? ` · ${blocker.component}` : ''}</small>
                    {(blocker.quantity || blocker.lotSerial) && <small>{blocker.quantity ? `Qty ${blocker.quantity}` : ''}{blocker.quantity && blocker.lotSerial ? ' · ' : ''}{blocker.lotSerial ? `Lot ${blocker.lotSerial}` : ''}</small>}
                  </div>
                  <div className={(blocker.kind === 'expired' || blocker.expired) ? 'tracking-expiration-date expired' : 'tracking-expiration-date'}>
                    <strong>{blockerDate(blocker)}</strong>
                    <small>{blockerTiming(blocker)}</small>
                  </div>
                </div>
              ))}
            </div>

            <div className="tracking-expiration-modal-actions">
              <button type="button" className="tracking-btn tracking-btn-secondary" onClick={() => setModalOpen(false)}>Got it</button>
              <button type="button" className="tracking-btn tracking-btn-primary" onClick={() => checkReadiness(false)}>Refresh inventory check</button>
            </div>
            <p className="tracking-expiration-footnote">Closing this warning does not clear the deployment stop. Checkout stays blocked until the live inventory standard is satisfied.</p>
          </section>
        </div>
      )}
    </>
  );
}
